export type DieColor = 'white' | 'red' | 'blue';
export type DieFaces = 6 | 8 | 10 | 12;

export interface Die {
  id: string;
  color: DieColor;
  faces: DieFaces;
  value: number | null;
}

export type CardCost =
  | { kind: 'sumMin'; min: number }
  | { kind: 'sumExact'; total: number }
  | { kind: 'sumMinWithColor'; min: number; color: DieColor; count: number }
  | {
      kind: 'set';
      pattern: 'threeOfAKind' | 'threeColors' | 'pair' | 'run3';
    };

export type Reward =
  | {
      kind: 'addDie';
      die: { color: DieColor; faces: DieFaces };
    }
  | { kind: 'token'; token: 'reroll' | 'convertColor' };

export interface Card {
  id: string;
  row: 0 | 1 | 2 | 3 | 4;
  col: number;
  cost: CardCost;
  rewards: Reward[];
  placedDice: Die[];
  acquired: boolean;
}

export type CardTemplate = Omit<
  Card,
  'placedDice' | 'acquired' | 'row' | 'col'
> & { weight?: number };

export type TurnPhase =
  | 'Drawing'
  | 'Rolling'
  | 'AdjustingPerks'
  | 'Placing'
  | 'Resolving'
  | 'TurnEnd'
  | 'Win'
  | 'Loss';

export interface PerksRemaining {
  adjust: number;
  reroll: number;
}

export interface Tokens {
  reroll: number;
  convertColor: number;
}

export interface GridPos {
  row: number;
  col: number;
}

export interface GameState {
  bag: Die[];
  hand: Die[];
  pyramid: Card[];
  lastAcquired: GridPos | null;
  acquiredThisTurn: number;
  tokens: Tokens;
  perksRemaining: PerksRemaining;
  phase: TurnPhase;
  rngState: number;
  seed: number;
}

export const ROW_WIDTHS = [5, 4, 3, 2, 1] as const;
export const TOTAL_SLOTS = ROW_WIDTHS.reduce((a, b) => a + b, 0);

export type Action =
  | { type: 'newGame'; seed?: number }
  | { type: 'startTurn' }
  | { type: 'roll' }
  | { type: 'adjustDie'; dieId: string; delta: 1 | -1 }
  | { type: 'rerollDie'; dieId: string }
  | { type: 'placeDie'; dieId: string; cardId: string }
  | { type: 'unplaceDie'; dieId: string }
  | { type: 'finishPlacing' }
  | {
      type: 'acquireCard';
      cardId: string;
      tieBreakerDieId?: string;
    }
  | { type: 'endTurn' }
  | { type: 'useToken'; token: 'reroll' | 'convertColor'; dieId: string; toColor?: DieColor };
